import {
  Asset,
  Portfolio,
  AdjustmentScenario,
  CreateAdjustmentRequest,
  UpdateAdjustmentRequest,
  SimulateScenarioRequest,
  ScenarioResultResponse,
  RegimeTemplateListItemDto,
  ApiError,
  ApiErrorResponse,
} from '@/types/api'

/**
 * API Client for backend communication
 * 
 * Features:
 * - Automatic Decimal string parsing (backend sends Decimal as string)
 * - Consistent error handling with custom ApiError class
 * - Environment-based base URL configuration
 */
export class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string = '/api') {
    this.baseUrl = baseUrl
  }

  // =============================================================================
  // Private Helpers
  // =============================================================================

  private async fetch<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const errorData: ApiErrorResponse = await response.json().catch(() => ({
        message: 'An unexpected error occurred',
      }))
      
      throw new ApiError(
        errorData.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData.code,
        errorData.details
      )
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T
    }

    const data = await response.json()
    return this.parseDecimalFields(data)
  }

  /**
   * Recursively parse Decimal strings to numbers
   * Backend sends Decimal values as strings for precision
   */
  private parseDecimalFields<T>(obj: unknown): T {
    if (typeof obj === 'string' && this.isDecimalString(obj)) {
      return parseFloat(obj) as T
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => this.parseDecimalFields<unknown>(item)) as T
    }
    if (obj && typeof obj === 'object') {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key,
          this.parseDecimalFields<unknown>(value),
        ])
      ) as T
    }
    return obj as T
  }

  /**
   * Check if a string looks like a decimal number
   */
  private isDecimalString(str: string): boolean {
    // Match numbers like "123.45", "-67.89", "0.00"
    return /^-?\d+\.\d+$/.test(str)
  }

  // =============================================================================
  // Asset Endpoints
  // =============================================================================

  /**
   * GET /api/assets
   * List all assets with classifications
   */
  async getAssets(): Promise<Asset[]> {
    return this.fetch<Asset[]>('/assets')
  }

  // =============================================================================
  // Portfolio Endpoints
  // =============================================================================

  /**
   * GET /api/portfolio
   * Get current portfolio with positions
   */
  async getPortfolio(): Promise<Portfolio> {
    return this.fetch<Portfolio>('/portfolio')
  }

  // =============================================================================
  // Adjustment Scenario Endpoints
  // =============================================================================

  /**
   * GET /api/adjustments
   * List all adjustment scenarios
   */
  async getAdjustments(): Promise<AdjustmentScenario[]> {
    return this.fetch<AdjustmentScenario[]>('/adjustments')
  }

  /**
   * POST /api/adjustments
   * Create a new adjustment scenario
   */
  async createAdjustment(
    data: CreateAdjustmentRequest
  ): Promise<AdjustmentScenario> {
    return this.fetch<AdjustmentScenario>('/adjustments', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * PATCH /api/adjustments/:id
   * Update an existing adjustment scenario
   */
  async updateAdjustment(
    id: string,
    data: UpdateAdjustmentRequest
  ): Promise<AdjustmentScenario> {
    return this.fetch<AdjustmentScenario>(`/adjustments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * DELETE /api/adjustments/:id
   * Delete an adjustment scenario
   */
  async deleteAdjustment(id: string): Promise<void> {
    return this.fetch<void>(`/adjustments/${id}`, {
      method: 'DELETE',
    })
  }

  // =============================================================================
  // Scenario Simulation Endpoints
  // =============================================================================

  /**
   * POST /api/simulate
   * Run a scenario simulation
   */
  async simulateScenario(
    request: SimulateScenarioRequest
  ): Promise<ScenarioResultResponse> {
    return this.fetch<ScenarioResultResponse>('/simulate', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  // =============================================================================
  // Regime Template Endpoints
  // =============================================================================

  /**
   * GET /api/regimes/templates
   * List available regime templates
   */
  async getRegimeTemplates(): Promise<RegimeTemplateListItemDto[]> {
    return this.fetch<RegimeTemplateListItemDto[]>('/regimes/templates')
  }
}

// =============================================================================
// Singleton Export
// =============================================================================

/**
 * Singleton API client instance
 * Uses VITE_API_BASE_URL from environment, defaults to '/api'
 */
export const apiClient = new ApiClient(
  import.meta.env.VITE_API_BASE_URL || '/api'
)
